<template>
    <DialogLayout :class-name="'edit-remark-dialog'" @close="handleClose">
        <template #title>{{ dialogTitle }}</template>
        <template #default>
            <div v-if="description" class="dialog-description">{{ description }}</div>
            <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'left'" :label-width="fieldLabel ? '100px' : '0'"
                class="edit-remark-form">
                <el-form-item :label="fieldLabel" prop="remark">
                    <el-input v-model="formData.remark" type="textarea" :placeholder="t('common.pleaseEnter')" :rows="4" :maxlength="maxlength"
                        show-word-limit />
                </el-form-item>
            </el-form>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="primary" @click="handleConfirm">
                {{ t('common.save') }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { type FormInstance, type FormRules } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import type { EditRemarkDialogProps } from './edit-remark-dialog-types'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const props = defineProps<EditRemarkDialogProps>()

const emit = defineEmits<{
    (e: 'confirm', remark: string): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()

// 标题与 label 支持外部传入，不传时用默认文案
const dialogTitle = computed(() => props.title || t('candidate.editRemark'))
const fieldLabel = computed(() => props.label ?? t('candidate.remarkInfo'))

// 表单数据
const formData = reactive<{
    remark: string
}>({
    remark: ''
})

// 表单验证规则
const rules: FormRules = {
    // 备注非必填，不需要验证规则
}

// 初始化表单数据（回显）
onMounted(() => {
    if (props.remark) {
        formData.remark = props.remark
    }
})

// 确认保存
const handleConfirm = async () => {
    if (!formRef.value) return

    try {
        await formRef.value.validate()
    } catch {
        return
    }
    if (props.onSubmit) {
        props.onSubmit(formData.remark);
    } else {
        emit('confirm', formData.remark || '')
    }

}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
// 说明文案放在内容区：与标题、表单的间距都是 16px（标题下方的 16px 来自 header 的 padding-bottom）
.dialog-description {
    margin-bottom: 16px;
    font-size: 14px;
    line-height: 20px;
    color: #495057;
}

.edit-remark-form {

    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: 35px;
        text-align: right;
        padding-right: 12px;
        margin-bottom: 0px;

        &::before {
            display: none;
        }
    }

    :deep(.el-textarea) {
        .el-textarea__inner {
            font-size: 14px;
            color: #495057;
            line-height: 22px;
            resize: none;

            &::placeholder {
                color: #C9D3D9;
            }
        }
    }

    :deep(.el-input__count) {
        font-size: 12px;
        color: #86909C;
        background: transparent;
    }
}

:deep(.edit-remark-dialog) {
    width: 512px;

   
}
</style>

<style lang="scss">
.edit-remark-dialog {
    width: 512px !important;
    .dialog-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            padding: 20px 30px;
    
            .el-button {
                min-width: 80px;
            }
        }
}
</style>

